import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';

interface FreeAgentContact {
  id: number;
  organisationName?: string;
  firstName?: string;
  lastName?: string;
  jiraKey?: string; // stored in 'mobile'
}

interface JiraProject {
  id: string | number;
  key: string;
  name: string;
}

const JiraFreeAgentSetupPage: React.FC = () => {
  const [step, setStep] = useState<number>(1);

  const [selectedRetainerJiraIssueSummary, setSelectedRetainerJiraIssueSummary] = useState<string>('');

  const [isConnectedDropdownDisabled, setIsConnectedDropdownDisabled] = useState(false);
  const [isUnconnectedDropdownDisabled, setIsUnconnectedDropdownDisabled] = useState(false);

  // For the connected dropdown, track its chosen value
  const [connectedDropdownValue, setConnectedDropdownValue] = useState<string>('');
  // For the unconnected dropdown
  const [unconnectedDropdownValue, setUnconnectedDropdownValue] = useState<string>('');

  // ===========================
  // 1) Basic data for contact selection
  // ===========================
  const [freeAgentContacts, setFreeAgentContacts] = useState<FreeAgentContact[]>([]);
  const [connectedContacts, setConnectedContacts] = useState<FreeAgentContact[]>([]);
  const [unconnectedContacts, setUnconnectedContacts] = useState<FreeAgentContact[]>([]);

  // The user’s chosen contact
  const [selectedContactId, setSelectedContactId] = useState<number | null>(null);
  // We’ll store the entire contact object once selected, to easily check if it has a JiraKey
  const [selectedContact, setSelectedContact] = useState<FreeAgentContact | null>(null);

  // If a contact is unconnected, we let them link to a Jira project
  // But we also do the same if the contact is connected but wants to update
  const [showLinkJiraModal, setShowLinkJiraModal] = useState<boolean>(false);

  // Lists and states for linking to an existing/new Jira project
  const [allJiraProjects, setAllJiraProjects] = useState<JiraProject[]>([]);
  const [selectedJiraProjectKey, setSelectedJiraProjectKey] = useState<string>('');
  const [newJiraProjectKey, setNewJiraProjectKey] = useState<string>('');
  const [newJiraProjectName, setNewJiraProjectName] = useState<string>('');
  const [projectStartDate, setProjectStartDate] = useState<string>('');
  const [projectEndDate, setProjectEndDate] = useState<string>('');

  // ===========================
  // 2) Retainer logic states
  // ===========================
  const [isRetainer, setIsRetainer] = useState<boolean | null>(null);
  const [retainerProjects, setRetainerProjects] = useState<any[]>([]);
  const [selectedRetainerProjectId, setSelectedRetainerProjectId] = useState<number | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [retainerFaTasks, setRetainerFaTasks] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedRetainerFaTaskId, setSelectedRetainerFaTaskId] = useState<number | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [useGenericTask, setUseGenericTask] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [noFaTaskFound, setNoFaTaskFound] = useState<boolean>(false);

  const [retainerProjectName, setRetainerProjectName] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [retainerJiraIssues, setRetainerJiraIssues] = useState<any[]>([]);
  const [selectedRetainerJiraIssueKey, setSelectedRetainerJiraIssueKey] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loadingJiraIssues, setLoadingJiraIssues] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [hasCorrespondingTask, setHasCorrespondingTask] = useState<boolean | null>(null);

  // ===========================
  // 3) Non-retainer logic
  // ===========================
  const [freeAgentProjects, setFreeAgentProjects] = useState<any[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [projectMode, setProjectMode] = useState<'existing' | 'new'>('existing');
  const [newProjectName, setNewProjectName] = useState<string>('');
  const [createdProjectId, setCreatedProjectId] = useState<number | null>(null);

  // The new local piece of state to track whether we've re-fetched at step 4:
  const [step4ProjectsLoaded, setStep4ProjectsLoaded] = useState<boolean>(false);

  // ===========================
  // 4) Jira epic/issue creation
  // ===========================
  const [jiraEpicName, setJiraEpicName] = useState<string>('');
  const [createdEpicKey, setCreatedEpicKey] = useState<string>('');
  const [issueSummary, setIssueSummary] = useState<string>('');
  const [createdIssueKey, setCreatedIssueKey] = useState<string>('');

  // ===========================
  // 5) General loading/error
  // ===========================
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // For the "pick or create issue" step:
  const [epicIssues, setEpicIssues] = useState<any[]>([]);
  const [showExistingIssues, setShowExistingIssues] = useState<boolean>(false);
  const [selectedIssueKey, setSelectedIssueKey] = useState<string>('');

  // For the "check FreeAgent task" step:
  const [faTasksForIssueCheck, setFaTasksForIssueCheck] = useState<any[]>([]);
  const [loadingFaCheck, setLoadingFaCheck] = useState<boolean>(false);
  const [foundTask, setFoundTask] = useState<any | null>(null); // store the found FreeAgent task if any

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [retainerProjectData, setRetainerProjectData] = useState<any | null>(null);
  const [retainerExceeded, setRetainerExceeded] = useState<boolean>(false);
  const [hoursPerMonth, setHoursPerMonth] = useState<number>(0);
  const [actualHoursUsed, setActualHoursUsed] = useState<number>(0);

  const [checkingEpic, setCheckingEpic] = useState(false);
  const [epicKey, setEpicKey] = useState<string>('');
  const [doneCheckingEpic, setDoneCheckingEpic] = useState(false);

  const [issues, setIssues] = useState<any[]>([]);
  const [showExisting, setShowExisting] = useState(false);
  const [localIssueSummary, setLocalIssueSummary] = useState('');

  const [localSelectedIssueKey, setLocalSelectedIssueKey] = useState<string>('');

  const [epicCheckTriggered, setEpicCheckTriggered] = useState(false);

  // -- For retainer Step 7 auto-create logic --
  const [retainerStep7Initialized, setRetainerStep7Initialized] = useState(false);
  const [retainerStep7Loading, setRetainerStep7Loading] = useState(false);
  const [retainerStep7Error, setRetainerStep7Error] = useState('');
  const [retainerStep7CreatedTask, setRetainerStep7CreatedTask] = useState<any | null>(null);

  // Basic Auth
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const authHeader = `Basic ${btoa(`${username}:${password}`)}`;
  const getRequestErrorMessage = (err: any, fallback: string) => {
    return err?.response?.data?.error
      || err?.response?.data?.message
      || err?.message
      || fallback;
  };

  const nonRetainerTasks = [
    'Business Analysis',
    'Client Meeting',
    'Project Management',
    'Testing',
    'Unbillable (Goodwill)',
    'Design',
    'Content Population',
    'T&M Development',
  ];

  const autoCreateRetainerTaskForIssue = async () => {
    if (!selectedRetainerProjectId || !selectedRetainerJiraIssueKey) return;
  
    try {
      setRetainerStep7Loading(true);
      setRetainerStep7Error('');
  
      // 1) Fetch existing tasks in the FreeAgent project
      const resp = await axios.get(
        `${process.env.REACT_APP_API_URL}/freeagent-projects/${selectedRetainerProjectId}/tasks`,
        { headers: { Authorization: authHeader } }
      );
      const tasks = resp.data || [];
  
      // 2) Check if a "[ISSUE-KEY]" task already exists
      const existing = tasks.find((t: any) =>
        t.name && t.name.includes(selectedRetainerJiraIssueKey)
      );
  
      if (existing) {
        // Already found one, so store it
        setRetainerStep7CreatedTask(existing);
      } else {
        // 3) Create a new one using the local summary from step 5
        //    e.g. "[MYPROJ-123] My Issue Summary"
        const finalSummary = selectedRetainerJiraIssueSummary || '(No Summary)';
        const taskName = `[${selectedRetainerJiraIssueKey}] ${finalSummary}`;
  
        const createBody = {
          name: taskName,
          isBillable: false,
          billingRate: '85',
          billingPeriod: 'hour',
          status: 'Active',
          jiraKey: selectedRetainerJiraIssueKey,
        };
        const createResp = await axios.post(
          `${process.env.REACT_APP_API_URL}/freeagent-projects/${selectedRetainerProjectId}/tasks`,
          createBody,
          { headers: { Authorization: authHeader, 'Content-Type': 'application/json' } }
        );
        setRetainerStep7CreatedTask(createResp.data);
      }
    } catch (err: any) {
      setRetainerStep7Error(`Failed to auto-create retainer task: ${err.message}`);
    } finally {
      setRetainerStep7Loading(false);
    }
  };

  const ensureRetainerEpic = async () => {

    if (retainerProjectName) {

      console.log("LOOKING FOR RETAINER EPIC")

      setCheckingEpic(true);
      setError('');

      try {
        console.log("TRYING")
        // 1) See if there's already an epic matching retainerProjectName
        const epicName = retainerProjectName; 
        // fetch existing epics for the Jira project:
        const findResp = await axios.get(
          `${process.env.REACT_APP_API_URL}/jira-epics?projectKey=${selectedJiraProjectKey}`,
          { headers: { Authorization: authHeader } }
        );
        const existingEpics = findResp.data; // array of epics

        // see if any epic has summary === retainerProjectName
        const existing = existingEpics.find((e: any) => e.summary === epicName);

        if (existing) {

          console.log("EXISTING")

          // We have the epic already
          setEpicKey(existing.key);
        } else {

          console.log("NEED TO CREATE")

          // 2) Create the epic automatically
          const createResp = await axios.post(
            `${process.env.REACT_APP_API_URL}/jira-epics`,
            {
              projectKey: selectedJiraProjectKey,
              epicName: epicName,
              summary: epicName,
              description: `Auto-created retainer epic for ${epicName}`,
            },
            { headers: { Authorization: authHeader, 'Content-Type': 'application/json' } }
          );
          setEpicKey(createResp.data.key);
        }
      } catch (err: any) {
        setError(`Failed to ensure retainer epic: ${getRequestErrorMessage(err, 'Request failed')}`);
      } finally {
        setCheckingEpic(false);
        setDoneCheckingEpic(true);
      }
    }
  };

  useEffect(() => {
    // We only want to run once we land on step===7, retainer===true, etc.
    // Also check if we've not run it already (retainerStep7Initialized).
    if (
      step === 7 &&
      isRetainer === true &&
      !retainerStep7Initialized &&
      selectedRetainerProjectId &&
      selectedRetainerJiraIssueKey
    ) {
      setRetainerStep7Initialized(true);
      autoCreateRetainerTaskForIssue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    step,
    isRetainer,
    retainerStep7Initialized,
    selectedRetainerProjectId,
    selectedRetainerJiraIssueKey
  ]);

  useEffect(() => {
    if (
      step === 4 &&
      isRetainer === true &&
      selectedRetainerProjectId &&
      retainerProjectName && // now watch for retainerProjectName no longer being empty
      !epicCheckTriggered &&
      !doneCheckingEpic
    ) {
      setEpicCheckTriggered(true);
      ensureRetainerEpic();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    step,
    isRetainer,
    selectedRetainerProjectId,
    retainerProjectName,
    epicCheckTriggered,
    doneCheckingEpic,
  ]);

  useEffect(() => {
    if (doneCheckingEpic && epicKey && epicKey !== createdEpicKey) {
      setCreatedEpicKey(epicKey);
    }
  }, [doneCheckingEpic, epicKey, createdEpicKey]);
  
  // Step-based side effect: re-fetch at step 4
  useEffect(() => {
    // If we just transitioned to step 4, do a re-fetch
    // and only then set step4ProjectsLoaded to true
    const reFetchAtStep4 = async () => {
      if (step === 4) {
        setStep4ProjectsLoaded(false); // reset
        // If we have a `selectedContactId`, or however you track
        // which contact's projects to fetch:
        if (selectedContactId) {
          await fetchProjectsForContact(selectedContactId);
        }
        // Now we're done
        setStep4ProjectsLoaded(true);
      }
    };

    reFetchAtStep4();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (!selectedProjectId || step !== 6) return;
    setLoadingFaCheck(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/freeagent-projects/${selectedProjectId}/tasks`, {
        headers: { Authorization: authHeader },
      })
      .then((resp) => {
        setFaTasksForIssueCheck(resp.data);
        setLoadingFaCheck(false);
      })
      .catch((err) => {
        setError(`Failed to load tasks for final check: ${err.message}`);
        setLoadingFaCheck(false);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  // Once we have the tasks, see if there's one containing `createdIssueKey` in its name:
  useEffect(() => {
    if (faTasksForIssueCheck.length === 0 || !createdIssueKey || step !== 6) return;
    const existing = faTasksForIssueCheck.find((t: any) =>
      t.name && t.name.includes(createdIssueKey)
    );
    if (existing) {
      setFoundTask(existing);
    } else {
      setFoundTask(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faTasksForIssueCheck, createdIssueKey]);

  // --------------------------------------------------------------------------
  // Step-based effects
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (step === 1) {
      fetchFreeAgentContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    // If we're in the "non-retainer" path, at step 2, fetch normal FA projects
    console.log('TRY getting non retainer FA projects')
    console.log(step)
    console.log(selectedContactId)
    console.log(isRetainer)
    if (step === 3 && selectedContactId && isRetainer === false) {
      console.log('getting non retainer FA projects')
      fetchProjectsForContact(selectedContactId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedContactId, isRetainer]);

  // We'll fetch the single retainer project from your API
  const fetchSingleRetainerProject = async (projectId: number) => {
    setLoading(true);
    setError('');
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_API_URL}/freeagent-projects/${projectId}`,
        { headers: { Authorization: authHeader } }
      );
      // resp.data should match FreeAgentProjectResponse => includes .contract
      setRetainerProjectData(resp.data);

      if (resp.data && resp.data.contract) {
        const hours = resp.data.contract.hoursPerMonth || 0;
        const used = resp.data.contract.actualHoursUsed || 0;
        setHoursPerMonth(hours);
        setActualHoursUsed(used);

        // If used >= hours, we've exceeded
        setRetainerExceeded(used >= hours);
      }
    } catch (err: any) {
      setError(`Failed to load retainer project data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (step === 4 && selectedRetainerProjectId) {
      fetchSingleRetainerProject(selectedRetainerProjectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedRetainerProjectId]);

  // --------------------------------------------------------------------------
  // Fetch all FreeAgent contacts, then categorize
  // --------------------------------------------------------------------------
  const fetchFreeAgentContacts = () => {
    setLoading(true);
    setError('');
    axios
      .get(`${process.env.REACT_APP_API_URL}/freeagent-contacts`, {
        headers: { Authorization: authHeader },
      })
      .then((response) => {
        const contacts: FreeAgentContact[] = response.data.map((raw: any) => ({
          ...raw,
          // Convert the string ID to a number
          id: Number(raw.id),
        }));
        setFreeAgentContacts(contacts);

        // Regex for standard Jira key format
        const jiraKeyRegex = /^[A-Z]+$/;

        const connected = contacts.filter((c) => {
          // c.jiraKey must exist, not be empty, and match the pattern
          const jk = c.jiraKey ? c.jiraKey.trim() : '';
          return jiraKeyRegex.test(jk);
        });

        const unconnected = contacts.filter((c) => {
          // unconnected means either no jiraKey, empty string, or doesn't match the pattern
          const jk = c.jiraKey ? c.jiraKey.trim() : '';
          return !jiraKeyRegex.test(jk);
        });

        setConnectedContacts(connected);
        setUnconnectedContacts(unconnected);
      })
      .catch((err) => {
        setError(`Failed to load contacts: ${err.message}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // --------------------------------------------------------------------------
  // Step 1 UI: select from “connected” or “unconnected”
  // --------------------------------------------------------------------------
  const handleSelectConnectedContact = (contactId: number) => {
    const found = freeAgentContacts.find((c) => c.id === contactId) || null;
    setSelectedContactId(contactId);
    setSelectedContact(found);  
    // Default the newJiraProjectName to the contact's name:
    if (found) {
      // If the contact has an organisationName, use that as a default project name
      const defaultName = found.organisationName
        ? found.organisationName
        : `${found.firstName ?? ''} ${found.lastName ?? ''}`.trim();
      setNewJiraProjectName(defaultName);
  
      // If a jiraKey exists, set it as selectedJiraProjectKey
      if (found.jiraKey) {
        setSelectedJiraProjectKey(found.jiraKey);
      } else {
        // If no jiraKey, clear the field:
        setSelectedJiraProjectKey('');
      }
    }
  };

  const handleSelectUnconnectedContact = (contactId: number) => {
    const found = freeAgentContacts.find((c) => c.id === contactId) || null;
    setSelectedContactId(contactId);
    setSelectedContact(found);
  
    // Default the newJiraProjectName to the contact's name:
    if (found) {
      const defaultName = found.organisationName
        ? found.organisationName
        : `${found.firstName ?? ''} ${found.lastName ?? ''}`.trim();
      setNewJiraProjectName(defaultName);
    }
  };

  // Called when user hits “Link to Jira” or “Change Jira Project” button
  const handleLinkJiraProject = () => {
    setShowLinkJiraModal(true);
    fetchAllJiraProjects();
  };

  // --------------------------------------------------------------------------
  // Fetch all Jira projects
  // --------------------------------------------------------------------------
  const fetchAllJiraProjects = () => {
    setLoading(true);
    setError('');
    axios
      .get(`${process.env.REACT_APP_API_URL}/jira-projects`, {
        headers: { Authorization: authHeader },
      })
      .then((resp) => {
        setAllJiraProjects(resp.data);
      })
      .catch((err) => {
        setError(`Failed to load Jira projects: ${err.message}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // --------------------------------------------------------------------------
  // Link an existing Jira project to the contact => updates “mobile” field
  // --------------------------------------------------------------------------
  const handleLinkExistingProject = () => {
    if (!selectedContactId || !selectedJiraProjectKey) return;
    updateFreeAgentContactJiraKey(selectedContactId, selectedJiraProjectKey).then(() => {
      setShowLinkJiraModal(false);
      setStep(2);
    });
  };

  // --------------------------------------------------------------------------
  // Create new Jira project => update contact
  // --------------------------------------------------------------------------
  const handleCreateJiraProjectAndLink = () => {
    if (!newJiraProjectKey || !newJiraProjectName || !selectedContactId) return;
    setLoading(true);
    setError('');
  
    const body = {
      projectKey: newJiraProjectKey,
      name: newJiraProjectName,
      projectTypeKey: 'software',
      projectTemplateKey: 'com.pyxis.greenhopper.jira:gh-scrum-template',
    };
  
    axios
      .post(`${process.env.REACT_APP_API_URL}/jira-projects`, body, {
        headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
      })
      .then((resp) => {
        // If the response data includes the final project key, use it; otherwise default to what we sent
        const createdKey = resp.data.key || body.projectKey;
  
        // Make sure both newJiraProjectKey and selectedJiraProjectKey reflect this new key
        setNewJiraProjectKey(createdKey);
        setSelectedJiraProjectKey(createdKey);
  
        // Now update the FreeAgent contact so the 'mobile' field = the Jira key
        return updateFreeAgentContactJiraKey(selectedContactId, createdKey);
      })
      .then(() => {
        // Hide the modal and proceed
        setShowLinkJiraModal(false);
        setStep(2);
      })
      .catch((err) => {
        setError(`Failed to create + link project: ${err.message}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  // --------------------------------------------------------------------------
  // Helper to update contact’s “mobile” => Jira key
  // --------------------------------------------------------------------------
  const updateFreeAgentContactJiraKey = async (contactId: number, jiraKey: string) => {
    setLoading(true);
    setError('');

    const body = {
      jiraKey: jiraKey,
    };

    return axios
      .put(`${process.env.REACT_APP_API_URL}/freeagent-contacts/${contactId}`, body, {
        headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
      })
      .then(() => {
        // success
      })
      .catch((err) => {
        setError(`Failed to update contact: ${err.message}`);
        throw err;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // --------------------------------------------------------------------------
  // fetchProjectsForContact: non-retainer projects
  // --------------------------------------------------------------------------
  const fetchProjectsForContact = async (contactId: number) => {
    setLoading(true);
    setError('');
  
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/freeagent-contacts/${contactId}/projects`, 
        { headers: { Authorization: authHeader } }
      );
      setFreeAgentProjects(response.data);
    } catch (err: any) {
      setError(`Failed to load projects for contact ${contactId}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------------------------------
  // fetch retainer projects
  // --------------------------------------------------------------------------
  const fetchRetainerProjects = async (contactId: number): Promise<any[]> => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/freeagent-contacts/${contactId}/projects?customerSupport=true`,
        {
          headers: { Authorization: authHeader },
        }
      );
      setRetainerProjects(response.data);
      return response.data; // Return the data so it can be used with .then()
    } catch (err: any) {
      setError(`Failed to load retainer projects for contact ${contactId}: ${err.message}`);
      return []; // Return an empty array on failure
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------------------------------
  // create freeAgent project
  // --------------------------------------------------------------------------
  const createFreeAgentProject = async () => {
    if (
      !selectedContactId || 
      !newProjectName || 
      (!projectStartDate && !newProjectName.startsWith('Customer Support')) || 
      (!projectEndDate && !newProjectName.startsWith('Customer Support'))
    ) {
      return; // or handle error
    }
  
    setLoading(true);
    setError('');
  
    const body = {
      name: newProjectName,
      contactId: selectedContactId,
      contactName: '',
      status: 'Active',
      currency: 'GBP',
      startsOn: projectStartDate,
      endsOn: projectEndDate,
    };
  
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/freeagent-projects`, body, {
        headers: {
          Authorization: authHeader,
          'Content-Type': 'application/json',
        },
      });
  
      // We'll store the new project ID in both state variables
      const newProjectId = response.data.id;
      setCreatedProjectId(newProjectId);
      setSelectedProjectId(newProjectId);
  
      // After creation, re-fetch the updated project list
      await fetchProjectsForContact(selectedContactId);
  
      // -----------------------------
      // ADD THIS BLOCK (Retainer check)
      // -----------------------------
      if (isRetainer) {
        // Set retainerProjectName to the newly created project’s name
        setRetainerProjectName(response.data.name || '');
  
        // Now do the missing tasks check:
        await checkAndCreateMissingRetainerTasks(newProjectId);
      } else {

        //create tasks for non retainer projects
        await checkAndCreateNonRetainerTasks(newProjectId);
      }
  
      // Now proceed
      setStep(4);
    } catch (err: any) {
      setError(`Failed to create project: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const proceedWithExistingProject = async () => {
    if (!selectedProjectId) return;
  
    try {
      setLoading(true);
  
      // Check and create missing non-retainer tasks
      await checkAndCreateNonRetainerTasks(selectedProjectId);
  
      // Proceed to the next step
      setCreatedProjectId(selectedProjectId);
      setStep(4);
    } catch (err: any) {
      setError(`Failed to process existing project: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------------------------------
  // Retainer path
  // --------------------------------------------------------------------------
  const handleRetainerYes = () => {
    if (!selectedContactId) return;
    setIsRetainer(true);
    fetchRetainerProjects(selectedContactId).then((projects) => {
      if (projects.length === 0) {
        // No retainer project exists, prompt user to create one
        setNewProjectName('Customer Support');
        setProjectMode('new');
        setStep(3); // Go to project creation step
      } else {
        setStep(3); // Proceed to retainer project selection
      }
    });
  };

  const handleRetainerNo = () => {
    setIsRetainer(false);
    setStep(3);
  };

  const checkAndCreateMissingRetainerTasks = async (projectId: number) => {
    const tasks = await fetchRetainerFreeagentTasks(projectId);
    const requiredTasks = [
      'Customer Support',
      'Customer Support (Outside SLA)',
      'Unbillable (Goodwill)',
    ];
  
    const missingTasks = requiredTasks.filter(
      (task) => !tasks.some((t: any) => t.name === task)
    );
  
    if (missingTasks.length > 0) {
      for (const taskName of missingTasks) {
        await createRetainerTask(projectId, taskName);
      }
    }
  };

  const proceedWithRetainerProject = async () => {
    if (!selectedRetainerProjectId) return;
  
    const selectedProject = retainerProjects.find((p) => String(p.id) === String(selectedRetainerProjectId))

    console.log("CHOICE OF RPOJECT")
    console.log(retainerProjects)
    console.log("project id")
    console.log(selectedRetainerProjectId)
    console.log ("SELECTED PROJECT")
    console.log(selectedProject)


    if (selectedProject) {
      setRetainerProjectName(selectedProject.name || '');
    }
  
    // Reuse the helper
    await checkAndCreateMissingRetainerTasks(selectedRetainerProjectId);
  
    // Proceed to the next step
    setStep(4);
  };

  const createRetainerTask = async (projectId: number, taskName: string) => {
    try {
      const body = {
        name: taskName,
        isBillable: taskName !== 'Unbillable (Goodwill)' && taskName !== 'Customer Support',
        billingRate: '85',
        billingPeriod: 'hour',
        status: 'Active',
      };
  
      await axios.post(`${process.env.REACT_APP_API_URL}/freeagent-projects/${projectId}/tasks`, body, {
        headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
      });
      console.log(`Task "${taskName}" created successfully.`);
    } catch (err: any) {
      console.error(`Failed to create task "${taskName}": ${err.message}`);
    }
  };

  const checkAndCreateNonRetainerTasks = async (projectId: number) => {
    try {
      const tasks = await fetchRetainerFreeagentTasks(projectId); // Reuse the fetch logic
      const missingTasks = nonRetainerTasks.filter(
        (task) => !tasks.some((t: any) => t.name === task)
      );
  
      if (missingTasks.length > 0) {
        for (const taskName of missingTasks) {
          const isBillable = taskName === 'T&M Development'; // Only T&M Development is billable
          await createTask(projectId, taskName, isBillable);
        }
      }
    } catch (err: any) {
      console.error(`Failed to create tasks for project ${projectId}: ${err.message}`);
    }
  };

  const createTask = async (projectId: number, taskName: string, isBillable: boolean) => {
    try {
      const body = {
        name: taskName,
        isBillable,
        billingRate: isBillable ? '85' : '0', // Assign a rate if billable
        billingPeriod: 'hour',
        status: 'Active',
      };
  
      await axios.post(`${process.env.REACT_APP_API_URL}/freeagent-projects/${projectId}/tasks`, body, {
        headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
      });
  
      console.log(`Task "${taskName}" created successfully.`);
    } catch (err: any) {
      console.error(`Failed to create task "${taskName}": ${err.message}`);
    }
  };
  
  const fetchRetainerFreeagentTasks = async (projectId: number) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/freeagent-projects/${projectId}/tasks`,
        {
          headers: { Authorization: authHeader },
        }
      );
      setRetainerFaTasks(response.data);
      return response.data;
    } catch (err: any) {
      setError(`Failed to load tasks for project ${projectId}: ${err.message}`);
      return [];
    }
  };
  
  // --------------------------------------------------------------------------
  // Render Step Logic
  // --------------------------------------------------------------------------
  console.log(selectedContact);
  const renderStep = () => {
    if (loading) return <Spinner animation="border" />;
    if (error) return <Alert variant="danger">{error}</Alert>;

    // =========================
    // STEP 1: SELECT CONTACT
    // =========================
    if (step === 1) {
    
      return (
        <div>
          <h2>Select a FreeAgent Contact</h2>
          <p><strong>Contacts With Jira Project Key (Connected):</strong></p>
    
          <Form.Select
            className="mb-3"
            value={connectedDropdownValue}
            onChange={(e) => {
              const val = e.target.value;
              setConnectedDropdownValue(val);
    
              // Convert to number if needed
              const numericVal = Number(val);
              if (numericVal > 0) {
                handleSelectConnectedContact(numericVal);
                setIsUnconnectedDropdownDisabled(true); // Disable unconnected dropdown
              } else {
                // Re-enable both dropdowns if default option is selected
                setIsConnectedDropdownDisabled(false);
                setIsUnconnectedDropdownDisabled(false);
              }
            }}
            disabled={isConnectedDropdownDisabled} // Disable logic
          >
            <option value="">-- Pick a contact (connected) --</option>
            {connectedContacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.organisationName || `${c.firstName} ${c.lastName}`} 
                {c.jiraKey ? ` (JiraKey=${c.jiraKey})` : ''}
              </option>
            ))}
          </Form.Select>
    
          <Form.Select
            className="mb-3"
            value={unconnectedDropdownValue}
            onChange={(e) => {
              const val = e.target.value;
              setUnconnectedDropdownValue(val);
    
              const numericVal = Number(val);
              if (numericVal > 0) {
                handleSelectUnconnectedContact(numericVal);
                setIsConnectedDropdownDisabled(true); // Disable connected dropdown
              } else {
                // Re-enable both dropdowns if default option is selected
                setIsConnectedDropdownDisabled(false);
                setIsUnconnectedDropdownDisabled(false);
              }
            }}
            disabled={isUnconnectedDropdownDisabled} // Disable logic
          >
            <option value="">-- Pick a contact (unconnected) --</option>
            {unconnectedContacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.organisationName || `${c.firstName} ${c.lastName}`}
              </option>
            ))}
          </Form.Select>
    
          {selectedContact && (
            <div style={{ border: '1px solid #ccc', padding: '1rem', marginTop: '1rem' }}>
              <p>
                <strong>Selected Contact:</strong>{' '}
                {selectedContact.organisationName ||
                  `${selectedContact.firstName} ${selectedContact.lastName}`}
                {selectedContact.jiraKey ? (
                  <>
                    {' '}
                    (Already linked to JiraKey <code>{selectedContact.jiraKey}</code>)
                  </>
                ) : null}
              </p>
    
              {/* 
                1) If user wants to link or re-link:
              */}
              <Button
                variant="info"
                className="me-3"
                onClick={handleLinkJiraProject}
              >
                {selectedContact.jiraKey
                  ? 'Change Jira Project'
                  : 'Link to Jira Project'}
              </Button>
    
              {/* 2) If a link exists, they can continue: */}
              <Button
                variant="primary"
                onClick={() => {
                  // **Prevent moving to step 2 if this contact has no Jira key**
                  if (!selectedContact.jiraKey) {
                    alert('You must link this contact to a Jira project before continuing.');
                    return;
                  }
                  setStep(2);
                }}
              >
                Continue
              </Button>
            </div>
          )}
    
          {/* “Link Jira” modal (inline) */}
          {showLinkJiraModal && (
            <div style={{ marginTop: '1rem', padding: '1rem', background: '#eee' }}>
              <h5>Link to an existing Jira Project</h5>
              <Form.Select
                className="mb-2"
                value={selectedJiraProjectKey}
                onChange={(e) => setSelectedJiraProjectKey(e.target.value)}
              >
                <option value="">-- Select a Jira Project --</option>
                {allJiraProjects.map((proj) => (
                  <option key={proj.id} value={proj.key}>
                    {proj.key} - {proj.name}
                  </option>
                ))}
              </Form.Select>
              <Button
                disabled={!selectedJiraProjectKey}
                onClick={handleLinkExistingProject}
                className="mb-3"
              >
                Link Existing
              </Button>
    
              <hr />
              <h5>Or Create a New Jira Project</h5>
              <Form.Control
                className="mb-2"
                placeholder="Project Key (e.g. TESTABC)"
                value={newJiraProjectKey}
                onChange={(e) => setNewJiraProjectKey(e.target.value)}
              />
              <Form.Control
                className="mb-2"
                placeholder="Project Name"
                value={newJiraProjectName}
                onChange={(e) => setNewJiraProjectName(e.target.value)}
              />
              <Button
                disabled={!newJiraProjectKey || !newJiraProjectName}
                onClick={handleCreateJiraProjectAndLink}
              >
                Create & Link
              </Button>
            </div>
          )}
        </div>
      );
    }

    // =========================
    // STEP 2: Is retainer?
    // =========================
    if (step === 2) {
      return (
        <div>
          <h2>Is this work part of a retainer?</h2>
          <Button variant="primary" disabled={!selectedContactId} onClick={handleRetainerYes}>
            Yes
          </Button>{' '}
          <Button variant="secondary" disabled={!selectedContactId} onClick={handleRetainerNo}>
            No
          </Button>
        </div>
      );
    }

    // RETAINER PATH
    if (isRetainer === true) {
      if (step === 3) {
        if (loading) {
          return <Spinner animation="border" />;
        }
        return (
          <div>
            <h2>{retainerProjects.length === 0 ? 'Create Retainer Project' : 'Select Retainer Project'}</h2>
    
            {retainerProjects.length === 0 ? (
              // Project creation form
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Retainer Project Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter project name (default: Customer Support)"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                  />
                </Form.Group>
                <Button
                  disabled={!newProjectName}
                  onClick={createFreeAgentProject}
                >
                  Create Project
                </Button>
              </>
            ) : (
              // Existing project selection dropdown
              <>
                <Form.Group>
                  <Form.Label>Retainer Project</Form.Label>
                  <Form.Select
                    value={selectedRetainerProjectId || ''}
                    onChange={(e) => setSelectedRetainerProjectId(Number(e.target.value))}
                  >
                    <option value="">-- Select a retainer project --</option>
                    {retainerProjects.map((proj) => (
                      <option key={proj.id} value={proj.id}>
                        {proj.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Button disabled={!selectedRetainerProjectId} onClick={proceedWithRetainerProject}>
                  Next
                </Button>
              </>
            )}
          </div>
        );
      }

      // ========================================================
      // STEP 4: Ensure "Customer Support" epic in Jira for this retainer
      // ========================================================
      //
      // We'll do two main things here:
      // 1) Check if there's already a Jira epic named exactly the same as retainerProjectName (or "Customer Support" + retainerProjectName).
      // 2) If not found, create it automatically (the user has no choice).
      // Then we auto-advance to step 5 for picking/creating a sub-task under that epic.

      if (step === 4 && isRetainer) {
        if (checkingEpic || loading) {
          return <Spinner />;
        }
        if (error) {
          return <Alert variant="danger">{error}</Alert>;
        }
      
        // If we have no epic yet, show "Ensuring..."
        if (!doneCheckingEpic) {
          return <p>Ensuring retainer epic exists...</p>;
        }
      
        // doneCheckingEpic = true
        if (!epicKey) {
          return <Alert variant="warning">No epic found or created?</Alert>;
        }
      
        // If we do have an epicKey, we might still be
        // waiting for `useEffect` to setCreatedEpicKey.
        // But you can just show epicKey directly:
        return (
          <div>
            <p>Epic ensured: <strong>{epicKey}</strong></p>
            <Button onClick={() => setStep(5)}>Continue</Button>
          </div>
        );
      }

      // ========================================================
      // STEP 5: Pick or Create a Jira Task under this epic
      // ========================================================
      //
      // - We remove the old final-step selection of retainer tasks in FreeAgent.
      // - Instead, user picks/creates a JIRA Task *now*, under that forced "Customer Support" epic.
      // - We'll store that chosen issue key in e.g. `selectedRetainerJiraIssueKey`.
      // - Then proceed to Step 6 (check usage).

      if (step === 5) {
        if (!createdEpicKey) {
          return (
            <div>
              <p>No epic key found yet. Please go back.</p>
              <Button onClick={() => setStep(4)}>Back</Button>
            </div>
          );
        }

        const fetchIssues = async () => {
          setLoading(true);
          setError('');
          try {
            const resp = await axios.get(
              `${process.env.REACT_APP_API_URL}/jira-epics/${createdEpicKey}/issues`,
              { headers: { Authorization: authHeader } }
            );
            setIssues(resp.data);
            setShowExisting(true);
          } catch (err: any) {
            setError(`Failed to fetch epic issues: ${err.message}`);
          } finally {
            setLoading(false);
          }
        };

        const createNewIssue = async () => {
          if (!localIssueSummary) return;
          setLoading(true);
          setError('');
        
          try {
            const body = {
              issueType: 'Task',
              summary: localIssueSummary,
              description: 'Retainer sub-task',
              epicKey: createdEpicKey,
              priority: 'Medium',
              projectKey: selectedJiraProjectKey,
            };
            const resp = await axios.post(`${process.env.REACT_APP_API_URL}/jira-issues`, body, {
              headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
            });
            
            // The API returns resp.data.key as the new issue key
            const newKey = resp.data.key;
            
            // Store the new key
            setSelectedRetainerJiraIssueKey(newKey);
        
            // Also store the user’s typed summary so we can use it later
            setSelectedRetainerJiraIssueSummary(localIssueSummary);
        
            // proceed
            setStep(6);
          } catch (err: any) {
            setError(`Failed to create new issue: ${err.message}`);
          } finally {
            setLoading(false);
          }
        };

        const selectExistingIssue = () => {
          if (!localSelectedIssueKey) return;
        
          const chosenIssue = issues.find((iss) => iss.key === localSelectedIssueKey);
          if (chosenIssue) {
            // Store the summary in local state
            setSelectedRetainerJiraIssueSummary(chosenIssue.summary || '');
          }
        
          setSelectedRetainerJiraIssueKey(localSelectedIssueKey);
          setStep(6);
        };

        if (loading) return <Spinner animation="border" />;
        if (error) return <Alert variant="danger">{error}</Alert>;

        return (
          <div>
            <h2>Pick or Create Retainer Task in Jira Epic {createdEpicKey}</h2>

            {!showExisting && (
              <Button onClick={fetchIssues}>View Existing Issues</Button>
            )}
            {showExisting && (
              <div style={{ border: '1px solid #ccc', padding: '1rem', marginTop: '1rem' }}>
                {issues.length === 0 && <p>No existing issues found.</p>}
                {issues.length > 0 && (
                  <>
                    <Form.Select
                      value={localSelectedIssueKey}
                      onChange={(e) => setLocalSelectedIssueKey(e.target.value)}
                    >
                      <option value="">-- Select an issue --</option>
                      {issues.map((iss) => (
                        <option key={iss.key} value={iss.key}>
                          {iss.key} - {iss.summary}
                        </option>
                      ))}
                    </Form.Select>
                    <Button
                      className="mt-2"
                      disabled={!localSelectedIssueKey}
                      onClick={selectExistingIssue}
                    >
                      Use Selected Issue
                    </Button>
                  </>
                )}
              </div>
            )}

            <div style={{ marginTop: '1rem', padding: '1rem', border: '1px solid #ccc' }}>
              <h5>Create a New Jira Issue</h5>
              <Form.Control
                placeholder="Issue Summary"
                value={localIssueSummary}
                onChange={(e) => setLocalIssueSummary(e.target.value)}
              />
              <Button className="mt-2" disabled={!localIssueSummary} onClick={createNewIssue}>
                Create Issue
              </Button>
            </div>
          </div>
        );
      }

      // ========================================================
      // STEP 6: Check if Retainer Hours are Exceeded
      // ========================================================
      //
      // - We do the same logic as your “fetchSingleRetainerProject” call.
      // - If exceeded => show big red warning instructing them to get client approval & attach to the Jira ticket.
      // - If not exceeded => proceed.

      if (step === 6) {
        if (!selectedRetainerProjectId || !selectedRetainerJiraIssueKey) {
          return (
            <div>
              <p>Missing retainer project or Jira issue selection. Please go back.</p>
              <Button onClick={() => setStep(5)}>Back</Button>
            </div>
          );
        }

        // We'll assume you already fetched retainer usage into (retainerExceeded, hoursPerMonth, actualHoursUsed)
        // If not, you can do it right here with fetchSingleRetainerProject(selectedRetainerProjectId).
        // For brevity, assume we've done it in a useEffect.

        if (loading) return <Spinner animation="border" />;
        if (error) return <Alert variant="danger">{error}</Alert>;

        return (
          <div>
            <h2>Retainer Usage Check</h2>
            <p><strong>Hours Per Month:</strong> {hoursPerMonth}</p>
            <p><strong>Hours Used:</strong> {actualHoursUsed}</p>
            {retainerExceeded ? (
              <Alert variant="danger">
                <strong>Retainer Limit EXCEEDED!</strong>
                <p>
                  You must get **written permission** from the client to proceed.
                  Attach that permission to the Jira ticket <code>{selectedRetainerJiraIssueKey}</code> as proof.
                </p>
              </Alert>
            ) : (
              <p>Your retainer limit has not been exceeded. You can log time normally.</p>
            )}

            <Button onClick={() => setStep(7)}>Continue</Button>
          </div>
        );
      }

      // ========================================================
      // STEP 7: Show final status once we've either created or found a corresponding task
      // ========================================================
      if (step === 7 && isRetainer) {
        // If we're still in the "loading" phase, show a spinner:
        if (retainerStep7Loading) {
          return <Spinner animation="border" />;
        }

        // If we encountered an error in autoCreateRetainerTaskForIssue:
        if (retainerStep7Error) {
          return <Alert variant="danger">{retainerStep7Error}</Alert>;
        }

        // Otherwise, we either found or created the task in "retainerStep7CreatedTask".
        return (
          <div>
            <h2>Retainer Setup Complete</h2>
            <p>
              Jira Issue: <strong>{selectedRetainerJiraIssueKey}</strong>
            </p>

            {retainerStep7CreatedTask && (
              <p>
                FreeAgent Task Created/Found:
                <strong> {retainerStep7CreatedTask.name}</strong><br />
                (ID: {retainerStep7CreatedTask.id})
              </p>
            )}

            {retainerExceeded && (
              <Alert variant="danger">
                The retainer limit is exceeded. You should have attached the client's
                written approval to {selectedRetainerJiraIssueKey} before logging time.
              </Alert>
            )}

            <p>
              You can now log time in Jira and in FreeAgent (against the newly created or found task).
            </p>

            <Button
              onClick={() => {
                resetAllStates();
                setStep(1);
              }}
            >
              Start Over
            </Button>
          </div>
        );
      }

    }

    // NON-RETAINER PATH
    else if (isRetainer === false) {
      if (step === 3) {
        return (
          <div>
            <h2>Set Up FreeAgent Project</h2>
      
            <Form.Check
              type="radio"
              label="Select Existing Project"
              name="projectMode"
              id="projectModeExisting"
              checked={projectMode === 'existing'}
              onChange={() => setProjectMode('existing')}
              className="mb-3"
            />
            {projectMode === 'existing' && (
              <Form.Group className="mb-3">
                <Form.Label>Existing Project</Form.Label>
                <Form.Select
                  value={selectedProjectId || ''}
                  onChange={(e) => setSelectedProjectId(Number(e.target.value))}
                >
                  <option value="">-- Select a project --</option>
                  {freeAgentProjects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
      
            <Form.Check
              type="radio"
              label="Create New Project"
              name="projectMode"
              id="projectModeNew"
              checked={projectMode === 'new'}
              onChange={() => setProjectMode('new')}
              className="mb-3"
            />
      
            {projectMode === 'new' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Project Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                  />
                </Form.Group>
      
                {/* The mandatory start/end dates */}
                <Form.Group className="mb-3">
                  <Form.Label>Project Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={projectStartDate}
                    onChange={(e) => setProjectStartDate(e.target.value)}
                  />
                </Form.Group>
      
                <Form.Group className="mb-3">
                  <Form.Label>Project End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={projectEndDate}
                    onChange={(e) => setProjectEndDate(e.target.value)}
                  />
                </Form.Group>
              </>
            )}
      
            {projectMode === 'existing' ? (
              <Button disabled={!selectedProjectId} onClick={proceedWithExistingProject}>
                Next
              </Button>
            ) : (
              <Button
                disabled={
                  !newProjectName ||
                  !projectStartDate || // Make sure these are not empty
                  !projectEndDate
                }
                onClick={createFreeAgentProject}
              >
                Create &amp; Next
              </Button>
            )}
          </div>
        );
      }
      
      // STEP 4
      if (step === 4) {
        // If we haven't completed the re-fetch yet, show a spinner or message
        if (!step4ProjectsLoaded) {
          return (
            <div>
              <p>Re-fetching the updated list of FreeAgent projects...</p>
              {/* or spinner */}
            </div>
          );
        }
  
        // Now we know freeAgentProjects is fresh, so do your normal logic:

        console.log("LOGGING")
        console.log(freeAgentProjects);
        console.log(selectedProjectId);

        const currentFaProject = freeAgentProjects.find(
          (p) => String(p.id) === String(selectedProjectId)
        );

        console.log(currentFaProject);

        console.log("END LOGGING")
  
        if (!currentFaProject) {
          return (
            <div>
              <p>Cannot find the selected FreeAgent project.</p>
              <Button onClick={() => setStep(3)}>Back</Button>
            </div>
          );
        }
        // 2) If the project has a "jiraKey", that means an epic is already connected.
        const existingEpicKey = currentFaProject.jiraKey || '';

        // Helper: remove any "[XYZ-123]" prefix from the project name.
        const stripOldEpicFromName = (projName: string) => {
          return projName.replace(/^\[[^\]]+\]\s*/, '').trim();
        };

        // Helper: update the project's name in FreeAgent to something like "[ABDT-1] My Project Name"
        const updateFaProjectName = async (faProjectId: string | number, newName: string) => {
          try {
            setLoading(true);
            setError('');
            await axios.put(
              `${process.env.REACT_APP_API_URL}/freeagent-projects/${faProjectId}`,
              { name: newName },
              {
                headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
              }
            );
            console.log('Project name updated to:', newName);
          } catch (err: any) {
            setError(`Failed to update project name: ${err.message}`);
          } finally {
            setLoading(false);
          }
        };

        // 3) Once we have an epic key (existing or newly created), rename the project and go to step 5.
        const handleEpicConnected = async (epicKey: string) => {
          if (!currentFaProject) return;
          const cleanName = stripOldEpicFromName(currentFaProject.name);
          const newName = `[${epicKey}] ${cleanName}`;
          await updateFaProjectName(currentFaProject.id, newName);

          setCreatedEpicKey(epicKey);
          setStep(5);
        };

        // CASE A: Epic already exists
        if (existingEpicKey) {
          return (
            <div>
              <h2>Epic Already Connected</h2>
              <p>
                This FreeAgent project is already linked to Jira epic:
                <strong> {existingEpicKey}</strong>.
              </p>
              {/* Optionally update the name if not done yet */}
              <Button
                onClick={() => {
                  // If you want to ensure the project name is correct, handle that:
                  handleEpicConnected(existingEpicKey); 
                }}
              >
                Continue
              </Button>
            </div>
          );
        }

        // CASE B: No epic yet => show "Create Epic" UI
        return (
          <div>
            <h2>Create a Jira Epic</h2>
            <Form.Group>
              <Form.Label>Epic Name</Form.Label>
              <Form.Control
                type="text"
                value={jiraEpicName}
                onChange={(e) => setJiraEpicName(e.target.value)}
              />
            </Form.Group>
            <Button
              disabled={!jiraEpicName}
              onClick={async () => {
                try {
                  setLoading(true);
                  setError('');

                  // Create the epic in Jira
                  const body = {
                    projectKey: selectedJiraProjectKey, // or another name if needed
                    epicName: jiraEpicName,
                    summary: jiraEpicName,
                    description: 'Auto-generated epic',
                  };

                  const resp = await axios.post(
                    `${process.env.REACT_APP_API_URL}/jira-epics`,
                    body,
                    {
                      headers: {
                        Authorization: authHeader,
                        'Content-Type': 'application/json',
                      },
                    }
                  );

                  const newEpicKey = resp.data.key;
                  // Rename the FA project => "[ABDT-1] OriginalName"
                  await handleEpicConnected(newEpicKey);
                } catch (err: any) {
                  setError(`Failed to create Jira epic: ${getRequestErrorMessage(err, 'Request failed')}`);
                } finally {
                  setLoading(false);
                }
              }}
            >
              Create Epic
            </Button>
          </div>
        );
      }

      // STEP 5
      if (step === 5) {
        // Make sure we have an epic
        if (!createdEpicKey) {
          return (
            <div>
              <p>No epic key found. Please go back and connect/create an epic first.</p>
              <Button onClick={() => setStep(4)}>Back</Button>
            </div>
          );
        }

        // 1) We’ll fetch existing issues for the epic (if not already done).
        const fetchIssuesForEpic = async () => {
          try {
            setLoading(true);
            setError('');
            const resp = await axios.get(
              `${process.env.REACT_APP_API_URL}/jira-epics/${createdEpicKey}/issues`,
              { headers: { Authorization: authHeader } }
            );
            setEpicIssues(resp.data);
            setShowExistingIssues(true);
          } catch (err: any) {
            setError(`Failed to load issues for epic: ${err.message}`);
          } finally {
            setLoading(false);
          }
        };

        // 2) Creating a new issue in the epic
        const handleCreateNewIssue = async () => {
          if (!issueSummary) return;
          try {
            setLoading(true);
            setError('');

            const body = {
              issueType: 'Task',
              summary: issueSummary,
              description: 'Created via the setup flow',
              priority: 'Medium',
              epicKey: createdEpicKey,
              projectKey: selectedJiraProjectKey, 
            };

            const response = await axios.post(
              `${process.env.REACT_APP_API_URL}/jira-issues`,
              body,
              {
                headers: {
                  Authorization: authHeader,
                  'Content-Type': 'application/json',
                },
              }
            );

            setCreatedIssueKey(response.data.key);
            setStep(6); // Move on to the new "check or create FA task" step
          } catch (err: any) {
            setError(`Failed to create Jira issue: ${err.message}`);
          } finally {
            setLoading(false);
          }
        };

        // 3) Selecting an existing issue
        const handleSelectExistingIssue = () => {
          if (!selectedIssueKey) return;
          setCreatedIssueKey(selectedIssueKey);
          setStep(6);
        };

        return (
          <div>
            <h2>Pick or Create a Jira Issue in Epic {createdEpicKey}</h2>

            {/* Show a button to load existing issues if not already done */}
            {!showExistingIssues && (
              <Button onClick={fetchIssuesForEpic} className="mb-3">
                View Existing Issues
              </Button>
            )}

            {/* If we've loaded existing issues, display them */}
            {showExistingIssues && (
              <div style={{ marginBottom: '1rem', border: '1px solid #ccc', padding: '1rem' }}>
                <h5>Select an Existing Issue</h5>
                {epicIssues.length > 0 ? (
                  <>
                    <Form.Group>
                      <Form.Label>Existing Issues</Form.Label>
                      <Form.Select
                        value={selectedIssueKey}
                        onChange={(e) => setSelectedIssueKey(e.target.value)}
                      >
                        <option value="">-- Select an issue --</option>
                        {epicIssues.map((issue: any) => (
                          <option key={issue.key} value={issue.key}>
                            {issue.key} - {issue.summary}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Button
                      className="mt-2"
                      disabled={!selectedIssueKey}
                      onClick={handleSelectExistingIssue}
                    >
                      Use Selected Issue
                    </Button>
                  </>
                ) : (
                  <p>No existing issues found in this epic.</p>
                )}
              </div>
            )}

            {/* Or create a new issue */}
            <div style={{ marginTop: '1rem', border: '1px solid #ccc', padding: '1rem' }}>
              <h5>Create a New Issue in {createdEpicKey}</h5>
              <Form.Group className="mb-2">
                <Form.Label>Issue Summary</Form.Label>
                <Form.Control
                  type="text"
                  value={issueSummary}
                  onChange={(e) => setIssueSummary(e.target.value)}
                />
              </Form.Group>
              <Button disabled={!issueSummary} onClick={handleCreateNewIssue}>
                Create New Issue
              </Button>
            </div>
          </div>
        );
      }


      // NEW STEP 6: Check if a corresponding FreeAgent task exists for the newly created or selected Jira issue
      if (step === 6) {
        // We have `createdIssueKey`, and we presumably have `selectedProjectId` (the FA project ID).
        // We'll fetch tasks from the relevant FA project and see if there's one containing `createdIssueKey`.

        // We'll define a function to create a new FA task if not found:
        const createFaTaskForIssue = async () => {
          if (!selectedProjectId || !createdIssueKey) return;
          try {
            setLoadingFaCheck(true);
      
            // Try to find the newly created or chosen issue in `epicIssues`.
            const chosenIssue = epicIssues.find((i: any) => i.key === createdIssueKey);
      
            // Fallback to the local `issueSummary` if we didn’t find it in `epicIssues`.
            // If user typed a brand-new summary, it’s stored in `issueSummary`.
            // If that’s also empty, default to '(No summary)'.
            const finalSummary = chosenIssue?.summary || issueSummary || '(No summary)';
            const taskName = `[${createdIssueKey}] ${finalSummary}`;
      
            const body = {
              name: taskName,
              isBillable: false,
              billingRate: '85',
              billingPeriod: 'hour',
              status: 'Active',
              jiraKey: createdIssueKey,
            };
      
            await axios.post(
              `${process.env.REACT_APP_API_URL}/freeagent-projects/${selectedProjectId}/tasks`,
              body,
              {
                headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
              }
            );
      
            // Refresh tasks to confirm:
            const resp = await axios.get(
              `${process.env.REACT_APP_API_URL}/freeagent-projects/${selectedProjectId}/tasks`,
              { headers: { Authorization: authHeader } }
            );
            setFaTasksForIssueCheck(resp.data);
      
          } catch (err: any) {
            setError(`Failed to create new FA task for issue: ${err.message}`);
          } finally {
            setLoadingFaCheck(false);
          }
        };

        // Now the UI:
        if (loadingFaCheck) return <Spinner animation="border" />;
        if (foundTask) {
          // If found => user can continue to final step
          return (
            <div>
              <h2>Corresponding FreeAgent Task Found</h2>
              <p>
                We found an existing FreeAgent task named <strong>{foundTask.name}</strong> that links to{' '}
                {createdIssueKey}.
              </p>
              <Button onClick={() => setStep(7)}>Next</Button>
            </div>
          );
        }

        // If not found => ask user to create
        return (
          <div>
            <h2>No Matching FreeAgent Task Found</h2>
            <p>
              We couldn't find a FreeAgent task referencing the Jira issue{' '}
              <strong>{createdIssueKey}</strong>.
            </p>
            <Button onClick={createFaTaskForIssue}>Create FreeAgent Task for this Issue</Button>
            <Button variant="secondary" onClick={() => setStep(7)}>
              Skip
            </Button>
          </div>
        );
      }

      // OLD Step 6 => rename to Step 7 (final)
      if (step === 7) {
        return (
          <div>
            <h2>Setup Complete - Please log time in FreeAgent</h2>
            <p>FreeAgent Project ID: {createdProjectId}</p>
            <p>Epic Key: {createdEpicKey}</p>
            <p>Issue Key: {createdIssueKey}</p>
            {/* If we stored `foundTask` or newlyCreatedTask, show the name: */}
            {foundTask && <p>Associated Task: {foundTask.name}</p>}
      
            <Button
              onClick={() => {
                resetAllStates();
                setStep(1);
              }}
            >
              Start Over
            </Button>
          </div>
        );
      }
    }

    return <div>Unknown step</div>;
  };

  // --------------------------------------------------------------------------
  // Reset all states
  // --------------------------------------------------------------------------
  const resetAllStates = () => {
    // Step
    setStep(1);
  
    // Connected/unconnected dropdowns
    setConnectedDropdownValue('');
    setUnconnectedDropdownValue('');
  
    // Basic data for contact selection
    setFreeAgentContacts([]);
    setConnectedContacts([]);
    setUnconnectedContacts([]);
    setSelectedContactId(null);
    setSelectedContact(null);
  
    // Linking Jira project
    setShowLinkJiraModal(false);
    setAllJiraProjects([]);
    setSelectedJiraProjectKey('');
    setNewJiraProjectKey('');
    setNewJiraProjectName('');
  
    // Retainer logic
    setIsRetainer(null);
    setRetainerProjects([]);
    setSelectedRetainerProjectId(null);
    setRetainerFaTasks([]);
    setSelectedRetainerFaTaskId(null);
    setUseGenericTask(false);
    setNoFaTaskFound(false);
    setRetainerProjectName('');
    setRetainerJiraIssues([]);
    setSelectedRetainerJiraIssueKey(null);
    setLoadingJiraIssues(false);
    setHasCorrespondingTask(null);
  
    // Non-retainer logic
    setFreeAgentProjects([]);
    setSelectedProjectId(null);
    setProjectMode('existing');
    setNewProjectName('');
    setCreatedProjectId(null);
  
    // Step-4 projects re-fetch tracking
    setStep4ProjectsLoaded(false);
  
    // Jira epic/issue creation
    setJiraEpicName('');
    setCreatedEpicKey('');
    setIssueSummary('');
    setCreatedIssueKey('');
  
    // General loading/error
    setLoading(false);
    setError('');
  
    // For "pick or create issue" step
    setEpicIssues([]);
    setShowExistingIssues(false);
    setSelectedIssueKey('');
  
    // For "check FreeAgent task" step
    setFaTasksForIssueCheck([]);
    setLoadingFaCheck(false);
    setFoundTask(null);
  };

  return <div className="container mt-5">{renderStep()}</div>;
};

export default JiraFreeAgentSetupPage;
